import { DEFAULT_SEED, type SeededRuntime } from "@/lib/seeded";
import { withEventMeta, type EventLogEntry } from "./events";
import {
  brandId,
  brokerId,
  customerId,
  leadId,
  orderId,
  productId,
  ticketId,
  type AffiliateTemplate,
  type Brand,
  type Broker,
  type Campaign,
  type Coupon,
  type Customer,
  type EntityCounters,
  type FlaggedPost,
  type Lead,
  type MockClock,
  type Order,
  type Product,
  type PushLog,
  type ScenarioConfig,
  type ScenarioId,
  type Settlement,
  type SupplierPayout,
  type Ticket,
  type YJSettings,
} from "./types";

const DAY = 86_400_000;
const HOUR = 3_600_000;

export type YJMockData = {
  brands: Brand[];
  products: Product[];
  brokers: Broker[];
  customers: Customer[];
  orders: Order[];
  leads: Lead[];
  tickets: Ticket[];
  coupons: Coupon[];
  campaigns: Campaign[];
  settlements: Settlement[];
  supplierPayouts: SupplierPayout[];
  affiliateTemplates: AffiliateTemplate[];
  flaggedPosts: FlaggedPost[];
  pushLog: PushLog[];
  events: EventLogEntry[];
  currentBrokerId: Broker["id"];
  currentBrandId: Brand["id"];
  currentScenario: ScenarioId;
  settings: YJSettings;
  clock: MockClock;
  counters: EntityCounters;
  scenarioConfig: ScenarioConfig;
};

function eventId(n: number) {
  return `evt_${String(n).padStart(4, "0")}`;
}

function makeOrderEvents(orders: Order[]) {
  let counter = 0;
  return orders.map((order) => {
    counter += 1;
    if (order.maai === 1) {
      return withEventMeta(eventId(counter), {
        type: "order.maai1.created",
        orderId: order.id,
        customerId: order.customerId,
        productId: order.productId,
        brokerId: order.brokerId,
        amount: order.amount,
        at: order.createdAt,
      });
    }
    if (order.maai === 2) {
      return withEventMeta(eventId(counter), {
        type: "order.maai2.closed",
        orderId: order.id,
        leadId: leadId(`L_${order.id}`),
        amount: order.amount,
        useHouseBrand: order.isHouseBrand === true || order.productId.startsWith("ph"),
        at: order.createdAt,
      });
    }
    const previousOrderIds = orders
      .filter((candidate) => candidate.customerId === order.customerId && candidate.createdAt < order.createdAt)
      .map((candidate) => candidate.id);
    return withEventMeta(eventId(counter), {
      type: "order.maai3plus.detected",
      orderId: order.id,
      previousOrderIds,
      maai: order.maai,
      at: order.createdAt,
    });
  });
}

export function makeSeed(runtime: SeededRuntime, scenario: ScenarioId = "normal"): YJMockData {
  const now = runtime.now();

  const brands: Brand[] = [
    { id: brandId("b1"), name: "Glow Beauty", gp: 60, status: "active", category: "ความงาม", bankAcc: "KTB 123-4-56789", regDoc: "ภพ.20 / 2566", joined: now - 120 * DAY, isHouse: false },
    { id: brandId("b2"), name: "Vita Plus", gp: 50, status: "active", category: "อาหารเสริม", bankAcc: "SCB 234-5-67890", regDoc: "ภพ.20 / 2567", joined: now - 80 * DAY, isHouse: false },
    { id: brandId("b3"), name: "Wellness Daily", gp: 40, status: "active", category: "อาหารเสริม", bankAcc: "KBank 345-6-78901", regDoc: "ภพ.20 / 2566", joined: now - 60 * DAY, isHouse: false },
    { id: brandId("b4"), name: "Pure Skin Lab", gp: 60, status: "active", category: "ความงาม", bankAcc: "BAY 456-7-89012", regDoc: "ภพ.20 / 2567", joined: now - 40 * DAY, isHouse: false },
    { id: brandId("b-house"), name: "Nice Care (House Brand)", gp: 100, status: "active", category: "House Brand", bankAcc: "-", regDoc: "-", joined: now - 200 * DAY, isHouse: true },
    { id: brandId("b5"), name: "Aromatic Co.", gp: 50, status: "pending", category: "หอม-อโรม่า", bankAcc: "TTB 567-8-90123", regDoc: "อยู่ระหว่างตรวจสอบ", joined: now - 5 * DAY, isHouse: false },
  ];

  const products = [
    { id: productId("p1"), brandId: brandId("b1"), name: "เซรั่มผิวกระจ่างใส VitaC 30ml", price: 890, stock: 240, sold: 1820, telesalesDiscount: 100 },
    { id: productId("p2"), brandId: brandId("b1"), name: "ครีมบำรุงผิว Hydra Boost 50g", price: 690, stock: 180, sold: 1140, telesalesDiscount: 80 },
    { id: productId("p3"), brandId: brandId("b2"), name: "วิตามินรวม Multi-Vita 60 เม็ด", price: 590, stock: 320, sold: 980, telesalesDiscount: 60 },
    { id: productId("p4"), brandId: brandId("b2"), name: "คอลลาเจน Premium 200g", price: 1290, stock: 90, sold: 720, telesalesDiscount: 150 },
    { id: productId("p5"), brandId: brandId("b3"), name: "น้ำมันปลา Omega-3 90 แคปซูล", price: 790, stock: 150, sold: 540, telesalesDiscount: 100 },
    { id: productId("p6"), brandId: brandId("b4"), name: "แผ่นมาส์กหน้า Bio-Cell 5 ชิ้น", price: 390, stock: 420, sold: 2200, telesalesDiscount: 40 },
    { id: productId("p7"), brandId: brandId("b4"), name: "เซรั่ม Retinol Night 20ml", price: 1190, stock: 60, sold: 380, telesalesDiscount: 200 },
    { id: productId("ph1"), brandId: brandId("b-house"), name: "Nice Care สบู่สมุนไพร 3 ก้อน", price: 290, stock: 999, sold: 3200, telesalesDiscount: 50 },
    { id: productId("ph2"), brandId: brandId("b-house"), name: "Nice Care ชาเขียวลดน้ำหนัก", price: 490, stock: 999, sold: 2400, telesalesDiscount: 80 },
    { id: productId("ph3"), brandId: brandId("b-house"), name: "Nice Care คอลลาเจนเปปไทด์", price: 690, stock: 999, sold: 1800, telesalesDiscount: 100 },
  ];

  const brokers: Broker[] = [
    { id: brokerId("br1"), name: "คุณทิพย์ สายแชร์", level: 7, exp: 4820, totalEarn: 24600, withdrawn: 18000, avatar: "ท", joined: now - 90 * DAY },
    { id: brokerId("br2"), name: "คุณก้อย แม่ค้าออนไลน์", level: 6, exp: 3120, totalEarn: 18400, withdrawn: 12000, avatar: "ก", joined: now - 75 * DAY },
    { id: brokerId("br3"), name: "คุณเอก รีวิวเวอร์", level: 9, exp: 6450, totalEarn: 42100, withdrawn: 30000, avatar: "อ", joined: now - 110 * DAY },
    { id: brokerId("br4"), name: "คุณนุ่น ติ๊กต็อกเกอร์", level: 5, exp: 2100, totalEarn: 11200, withdrawn: 8000, avatar: "น", joined: now - 50 * DAY },
    { id: brokerId("br5"), name: "คุณบาส เกมเมอร์", level: 4, exp: 1280, totalEarn: 6800, withdrawn: 4000, avatar: "บ", joined: now - 30 * DAY },
  ];

  const customers: Customer[] = [
    { id: customerId("c1"), name: "พรพิมล ส.", phone: "081-234-5678", addr: "เขตวัฒนา กรุงเทพฯ" },
    { id: customerId("c2"), name: "นภัส ก.", phone: "082-345-6789", addr: "เขตจตุจักร กรุงเทพฯ" },
    { id: customerId("c3"), name: "วรพล ม.", phone: "083-456-7890", addr: "เมือง เชียงใหม่" },
    { id: customerId("c4"), name: "สมหญิง ป.", phone: "084-567-8901", addr: "หาดใหญ่ สงขลา" },
    { id: customerId("c5"), name: "ปิยะ บ.", phone: "085-678-9012", addr: "บางพลี สมุทรปราการ" },
    { id: customerId("c6"), name: "กรรณิการ์ ส.", phone: "086-789-0123", addr: "เมือง ขอนแก่น" },
  ];

  const orders: Order[] = [];
  let orderCounter = 1000;
  const pushOrder = (custId: string, prodId: string, broker: string, maai: Order["maai"], daysAgo: number, status: Order["status"] = "delivered") => {
    const product = products.find((item) => item.id === productId(prodId));
    if (!product) throw new Error(`Missing seed product ${prodId}`);
    orderCounter += 1;
    orders.push({
      id: orderId(`o${orderCounter}`),
      customerId: customerId(custId),
      productId: productId(prodId),
      productName: product.name,
      brokerId: brokerId(broker),
      brandId: product.brandId,
      maai,
      amount: product.price,
      status,
      paymentMethod: (["COD", "QR", "CARD"] as const)[orderCounter % 3],
      isHouseBrand: product.brandId === brandId("b-house"),
      createdAt: now - daysAgo * DAY + runtime.int(1, DAY - 1),
    });
  };

  pushOrder("c1", "p1", "br1", 1, 25);
  pushOrder("c2", "p3", "br2", 1, 22);
  pushOrder("c3", "p6", "br3", 1, 20);
  pushOrder("c4", "p2", "br1", 1, 18);
  pushOrder("c5", "p4", "br3", 1, 14);
  pushOrder("c6", "p7", "br4", 1, 10);
  pushOrder("c1", "p5", "br2", 1, 7);
  pushOrder("c3", "p1", "br3", 1, 4);
  pushOrder("c1", "ph1", "br1", 2, 23);
  pushOrder("c2", "p4", "br2", 2, 20);
  pushOrder("c3", "ph2", "br3", 2, 18);
  pushOrder("c4", "ph3", "br1", 2, 15);
  pushOrder("c5", "ph2", "br3", 2, 12);
  pushOrder("c1", "p1", "br1", 3, 8);
  pushOrder("c2", "p3", "br2", 3, 5);
  pushOrder("c3", "ph2", "br3", 3, 2);
  pushOrder("c1", "ph1", "br1", 4, 1);

  const tickets: Ticket[] = [
    { id: ticketId("t1"), subject: "สินค้าชำรุดระหว่างจัดส่ง", category: "ผู้ซื้อ", priority: "high", status: "open", openedBy: "พรพิมล ส.", createdAt: now - 2 * HOUR, messages: [{ from: "customer", text: "เซรั่มที่ได้รับมาขวดบุบค่ะ ขอเปลี่ยนได้ไหม", at: now - 2 * HOUR }] },
    { id: ticketId("t2"), subject: "ค่าคอมไม้ 2 ยังไม่เข้า", category: "นายหน้า", priority: "medium", status: "open", openedBy: "คุณก้อย แม่ค้าออนไลน์", createdAt: now - 5 * HOUR, messages: [{ from: "broker", text: "เคสที่ปิดเมื่อวานยังไม่เห็นในกระเป๋าเลยค่ะ", at: now - 5 * HOUR }] },
    { id: ticketId("t3"), subject: "แก้ไขราคาสินค้า", category: "แบรนด์", priority: "low", status: "in_progress", openedBy: "Vita Plus", createdAt: now - DAY, messages: [{ from: "brand", text: "ขอปรับราคาคอลลาเจน Premium เป็น 1,390 บาทครับ", at: now - DAY }] },
    { id: ticketId("t4"), subject: "ลิงก์แชร์ใช้ไม่ได้", category: "นายหน้า", priority: "high", status: "closed", openedBy: "คุณบาส เกมเมอร์", createdAt: now - 3 * DAY, messages: [{ from: "broker", text: "ลิงก์เปิดไม่ขึ้นครับ", at: now - 3 * DAY }, { from: "support", text: "แก้ไขเรียบร้อยแล้วครับ ลองเช็คอีกครั้งนะครับ", at: now - 2 * DAY }] },
  ];

  const leads: Lead[] = orders
    .filter((order) => order.maai === 1)
    .slice(0, 4)
    .map((order) => ({
      id: leadId(`L_${order.id}`),
      orderId: order.id,
      customerId: order.customerId,
      productId: order.productId,
      brokerId: order.brokerId,
      status: "pending",
      assignedTo: null,
      createdAt: order.createdAt + 30 * 60_000,
      attempts: 0,
    }));

  const coupons: Coupon[] = [
    { id: "cp1", code: "TS-SAVE100", desc: "ส่วนลด 100 บาท สำหรับ Telesales", value: 100, scope: "telesales", used: 23, limit: 200, status: "active", createdAt: now - 7 * DAY },
    { id: "cp2", code: "WELCOME50", desc: "ลูกค้าใหม่ลด 50 บาท", value: 50, scope: "customer", used: 142, limit: 500, status: "active", createdAt: now - 14 * DAY },
  ];

  const campaigns: Campaign[] = [
    { id: "cm1", name: "Mid-Year Mega Sale", budget: 80_000, spent: 42_100, gmv: 218_000, status: "active", channel: "Facebook + TikTok", startsAt: now - 14 * DAY },
    { id: "cm2", name: "Affiliate Boost Q2", budget: 30_000, spent: 18_200, gmv: 96_400, status: "active", channel: "Affiliate Push", startsAt: now - 8 * DAY },
  ];

  const pushLog: PushLog[] = [
    { id: "n1", title: "โปรใหม่ Vita Plus +10% Commission", body: "วันนี้ - 23:59 เท่านั้น", sentAt: now - 4 * HOUR, reach: 1248 },
    { id: "n2", title: "อัพเดทระบบลิงก์ใหม่", body: "ลิงก์สั้นขึ้น แชร์ง่ายขึ้น", sentAt: now - DAY, reach: 1820 },
  ];

  const fixedCost = 86_000;
  const gatewayFee = 46_500;
  const marketingSpent = campaigns.reduce((sum, campaign) => sum + campaign.spent, 0);
  const gmv = orders.reduce((sum, order) => sum + order.amount, 0);
  const settlements: Settlement[] = [
    {
      id: "s1",
      periodStart: now - 30 * DAY,
      periodEnd: now - DAY,
      gmv,
      fixedCost,
      gatewayFee,
      marketingSpent,
      netProfit: gmv * 0.45 - fixedCost - gatewayFee - marketingSpent,
      paidOut: false,
    },
  ];

  const supplierPayouts: SupplierPayout[] = brands
    .filter((brand) => !brand.isHouse)
    .map((brand) => {
      const brandOrders = orders.filter((order) => order.brandId === brand.id);
      const gross = brandOrders.reduce((sum, order) => sum + order.amount, 0);
      const supplierShare = gross * (1 - brand.gp / 100);
      return { brandId: brand.id, gross, supplierShare, withdrawn: supplierShare * 0.6 };
    });

  const affiliateTemplates: AffiliateTemplate[] = [
    { id: "at1", name: "Beauty Bold", category: "ความงาม", uses: 412 },
    { id: "at2", name: "Health Trust", category: "อาหารเสริม", uses: 318 },
    { id: "at3", name: "Lifestyle Soft", category: "ทั่วไป", uses: 156 },
  ];

  const flaggedPosts: FlaggedPost[] = [
    { id: "fp1", brokerId: brokerId("br5"), excerpt: "...โปรดี ห้ามพลาด ของจัด!! ##spam##", reason: "ใช้คำซ้ำเชิง spam", reportedAt: now - HOUR, status: "pending" },
    { id: "fp2", brokerId: brokerId("br4"), excerpt: "...เห้ย กดเลยอย่ารอ...", reason: "พบคำหยาบ", reportedAt: now - 2 * HOUR, status: "pending" },
  ];

  const events = makeOrderEvents(orders);
  const counters: EntityCounters = {
    brand: 6,
    product: 10,
    broker: 5,
    customer: 6,
    order: orderCounter,
    lead: leads.length,
    ticket: tickets.length,
    coupon: coupons.length,
    campaign: campaigns.length,
    push: pushLog.length,
    event: events.length,
  };

  return {
    brands,
    products,
    brokers,
    customers,
    orders,
    leads,
    tickets,
    coupons,
    campaigns,
    settlements,
    supplierPayouts,
    affiliateTemplates,
    flaggedPosts,
    pushLog,
    events,
    currentBrokerId: brokerId("br1"),
    currentBrandId: brandId("b1"),
    currentScenario: scenario,
    settings: {
      coFounderSplit: { yiw: 55, dev: 45 },
      primaryColor: "#1E4FD9",
      theme: "light",
      gpDefault: 50,
    },
    clock: {
      seed: `${DEFAULT_SEED}:${scenario}`,
      now,
      stepMs: 60_000,
    },
    counters,
    scenarioConfig: {},
  };
}
